import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Lock, ArrowLeft, ArrowRight, Loader2, KeyRound, CreditCard, CheckCircle2 } from "lucide-react";
import { api, verifyAccessCode, submitSurvey, getConfig } from "@/lib/api";
import { SURVEY_SECTIONS, TOTAL_QUESTIONS } from "@/data/surveyQuestions";
import Scorecard from "@/components/Scorecard";

export default function Survey() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const refFromUrl = params.get("ref");
  const stateAccess = location.state;

  const [access, setAccess] = useState(
    stateAccess?.access_ref
      ? { access_type: stateAccess.access_type, access_ref: stateAccess.access_ref }
      : refFromUrl
      ? { access_type: "paid", access_ref: refFromUrl }
      : null
  );
  const [checking, setChecking] = useState(true);
  const [granted, setGranted] = useState(false);
  const [code, setCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    const defaults = {};
    SURVEY_SECTIONS.forEach((s) =>
      s.questions.forEach((q) => {
        if (q.type === "rating") defaults[q.id] = 5;
      })
    );
    return defaults;
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [config, setConfig] = useState({ calendly_url: "" });

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    const verify = async () => {
      if (!access) {
        setChecking(false);
        return;
      }
      try {
        await api.post("/access/verify", access);
        setGranted(true);
      } catch (e) {
        setGranted(false);
        toast.error("Access could not be verified. Please pay or enter a valid code.");
      } finally {
        setChecking(false);
      }
    };
    verify();
  }, [access]);

  const section = SURVEY_SECTIONS[step];
  const progress = useMemo(() => {
    const answered = Object.values(answers).filter((v) => v !== "" && v !== undefined && v !== null).length;
    return Math.round((answered / TOTAL_QUESTIONS) * 100);
  }, [answers]);

  const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

  const unlockWithCode = async () => {
    if (!code.trim()) return;
    setCodeLoading(true);
    try {
      const res = await verifyAccessCode(code.trim());
      setAccess({ access_type: "code", access_ref: res.access_ref });
      toast.success("Access granted!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired access code.");
    } finally {
      setCodeLoading(false);
    }
  };

  const validateSection = () => {
    for (const q of section.questions) {
      if (q.required && !String(answers[q.id] || "").trim()) {
        toast.error(`Please answer: ${q.label}`);
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateSection()) return;
    if (step < SURVEY_SECTIONS.length - 1) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      handleSubmit();
    }
  };

  const back = () => {
    if (step > 0) {
      setStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    if (!validateSection()) return;
    setSubmitting(true);
    try {
      const res = await submitSurvey({ ...access, answers });
      setResult(res.scorecard);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit survey. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Render states ----------
  if (checking) {
    return (
      <div className="min-h-screen bg-brand-bone flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-brand-bone py-14 px-4">
        <div className="max-w-4xl mx-auto mb-8">
          <div className="rounded-2xl border border-brand-green/20 bg-brand-green/5 p-5 md:p-6 text-center" data-testid="results-emailed-note">
            <div className="flex items-center justify-center gap-2 text-brand-green font-heading font-semibold">
              <CheckCircle2 className="w-5 h-5" /> Your results have been submitted and emailed to you.
            </div>
            <p className="mt-2 text-sm text-brand-ink/70 max-w-xl mx-auto">
              Please check your inbox. If you do not see them within a few minutes, check spam or contact support.
            </p>
            <p className="mt-1 text-sm text-brand-ink/50">Admin has been notified.</p>
          </div>
        </div>
        <Scorecard scorecard={result} calendlyUrl={config?.calendly_url} name={answers.q1} />
      </div>
    );
  }

  if (!granted) {
    return (
      <div className="min-h-screen bg-brand-bone flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-2xl border border-border bg-white p-8 shadow-sm text-center" data-testid="survey-locked">
          <div className="w-14 h-14 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green mx-auto mb-5">
            <Lock className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-2xl font-bold">Survey locked</h1>
          <p className="mt-3 text-brand-ink/70">
            Purchase the audit or enter your access code to unlock the survey.
          </p>

          <div className="mt-6 space-y-3 text-left">
            <Label htmlFor="survey-code">Access code</Label>
            <div className="flex gap-2">
              <Input
                id="survey-code"
                data-testid="survey-access-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter code"
                onKeyDown={(e) => e.key === "Enter" && unlockWithCode()}
              />
              <Button data-testid="survey-access-code-submit" onClick={unlockWithCode} disabled={codeLoading} className="rounded-lg bg-brand-green hover:bg-brand-greenDark text-white shrink-0">
                {codeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="my-5 flex items-center gap-3 text-xs text-brand-ink/40">
            <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
          </div>

          <Button data-testid="survey-goto-payment" onClick={() => navigate("/")} variant="outline" className="w-full rounded-full border-brand-gold text-brand-goldDark hover:bg-brand-gold/5">
            <CreditCard className="w-4 h-4 mr-2" /> Purchase the audit
          </Button>
        </div>
      </div>
    );
  }

  // ---------- Survey form ----------
  return (
    <div className="min-h-screen bg-brand-bone">
      <div className="sticky top-0 z-30 bg-brand-bone/90 backdrop-blur-md border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-heading font-semibold text-brand-green">{section.title}</span>
            <span className="text-brand-ink/50">Step {step + 1} of {SURVEY_SECTIONS.length}</span>
          </div>
          <div className="h-2 w-full bg-border rounded-full overflow-hidden">
            <div data-testid="survey-progress" className="h-full bg-brand-gold transition-all duration-500" style={{ width: `${((step + 1) / SURVEY_SECTIONS.length) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">{section.title}</h1>
            <p className="text-brand-ink/60 mt-1 mb-8">{section.subtitle}</p>

            <div className="space-y-7">
              {section.questions.map((q) => (
                <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10">
          <Button
            data-testid="survey-back-btn"
            variant="ghost"
            onClick={back}
            disabled={step === 0}
            className="text-brand-ink/60 disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            data-testid="survey-next-step"
            onClick={next}
            disabled={submitting}
            className="rounded-full bg-brand-green hover:bg-brand-greenDark text-white px-8"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
            ) : step === SURVEY_SECTIONS.length - 1 ? (
              "Get My Results"
            ) : (
              <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  const label = (
    <Label className="block text-base font-medium text-brand-ink mb-3">
      {q.label} {q.required && <span className="text-brand-gold">*</span>}
    </Label>
  );

  if (q.type === "rating") {
    const v = typeof value === "number" ? value : 5;
    return (
      <div data-testid={`q-${q.id}`}>
        {label}
        <div className="flex items-center gap-4">
          <Slider
            value={[v]}
            min={1}
            max={10}
            step={1}
            onValueChange={(val) => onChange(val[0])}
            className="flex-1"
            data-testid={`slider-${q.id}`}
          />
          <span className="w-12 text-center font-heading text-xl font-bold text-brand-green">{v}</span>
        </div>
        <div className="flex justify-between text-xs text-brand-ink/40 mt-2">
          <span>1 — Poor</span><span>10 — Excellent</span>
        </div>
      </div>
    );
  }

  if (q.type === "select") {
    return (
      <div data-testid={`q-${q.id}`}>
        {label}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {q.options.map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                data-testid={`option-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => onChange(opt)}
                className={`text-left text-sm rounded-xl border p-3 transition-all ${
                  active
                    ? "border-brand-green bg-brand-green/5 ring-2 ring-brand-green"
                    : "border-border bg-white hover:border-brand-green"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (q.type === "textarea") {
    return (
      <div data-testid={`q-${q.id}`}>
        {label}
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={`input-${q.id}`} className="focus-visible:ring-brand-gold" />
      </div>
    );
  }

  return (
    <div data-testid={`q-${q.id}`}>
      {label}
      <Input
        type={q.type === "email" ? "email" : "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`input-${q.id}`}
        className="focus-visible:ring-brand-gold"
      />
    </div>
  );
}
